import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain } from 'lucide-react';
import { BASE_URL } from '../config/api';

const TestTakingPage = () => {
  const navigate = useNavigate();
  const [testData, setTestData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const cachedTest = localStorage.getItem('currentTest');
    const studentDetails = localStorage.getItem('studentDetails');
    
    if (!cachedTest || !studentDetails) {
      navigate('/');
      return;
    }
    
    setTestData(JSON.parse(cachedTest));
  }, [navigate]);

  const handleSubmitTest = async () => {
    const testData = JSON.parse(localStorage.getItem('currentTest'));
    const studentDetails = JSON.parse(localStorage.getItem('studentDetails'));
    const testCode = localStorage.getItem('testCode');

    if (!testData || !studentDetails || !testCode) {
      navigate('/');
      return;
    }

    const submissionData = {
      testId: testData.id,
      studentName: studentDetails.name,
      studentEmail: studentDetails.email,
      testCode: testCode, // Include the test code in submission
      choiceList: Object.entries(selectedAnswers).map(([questionId, optionId]) => ({
        questionId: parseInt(questionId),
        optionId: parseInt(optionId)
      }))
    };

    // Log the submission data
    console.log('Submitting test data:', JSON.stringify(submissionData, null, 2));

    try {
      const response = await fetch(`${BASE_URL}/api/reports/submission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (response.ok) {
        // Clear test data from localStorage
        localStorage.removeItem('currentTest');
        localStorage.removeItem('studentDetails');
        localStorage.removeItem('testCode');
        navigate('/completion');
      } else {
        alert('Failed to submit test. Please try again.');
      }
    } catch (error) {
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!testData) return null;

  const currentQuestion = testData.questionList[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-indigo-500 text-white">
      <header className="container mx-auto py-6 px-4">
        <div 
          className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
        >
          <Brain className="h-8 w-8" />
          <span className="text-2xl font-bold">PMind</span>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Test Header */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 mb-6">
            <h1 className="text-2xl font-bold mb-2">{testData.title}</h1>
            <p className="text-white/70">{testData.subject.name}</p>
            <div className="mt-4 flex justify-between items-center">
              <span>Question {currentQuestionIndex + 1} of {testData.questionList.length}</span>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
            <p className="text-xl mb-6">{currentQuestion.text}</p>
            <div className="space-y-3">
              {currentQuestion.optionList.map((option, index) => (
                <button
                  key={option.id}
                  onClick={() => setSelectedAnswers({
                    ...selectedAnswers,
                    [currentQuestion.id]: option.id
                  })}
                  className={`w-full p-4 rounded-lg text-left transition ${
                    selectedAnswers[currentQuestion.id] === option.id
                      ? 'bg-yellow-400 text-purple-900'
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  {option.text}
                </button>
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                disabled={currentQuestionIndex === 0 || isSubmitting}
                className="px-6 py-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => {
                  if (currentQuestionIndex < testData.questionList.length - 1) {
                    setCurrentQuestionIndex(prev => prev + 1);
                  } else {
                    handleSubmitTest();
                  }
                }}
                disabled={isSubmitting}
                className="px-6 py-2 rounded-lg bg-yellow-400 text-purple-900 hover:bg-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-purple-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : currentQuestionIndex === testData.questionList.length - 1 ? (
                  'Submit'
                ) : (
                  'Next'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestTakingPage;
